import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  CalendarIcon,
  CheckCircle2,
  Flag,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  X,
} from "lucide-react";

import Modal from "../../../../../components/ui/Modal";
import { styles } from "./styles";
import { Box, Chip, Typography } from "@mui/material";
import { remittanceApi } from "../../../api/remittanceApi";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { AppStrings, logApiErrorToSentry } from "../../../utils";
import { toast } from "react-toastify";
import moment from "moment";
import Lottie from "react-lottie";
import * as animationData from "../../../../../assets/lottieAnimationsData/scanDocuments.json";
import { remittanceActions } from "../../../state/remittanceSlice";
import { WarningIcon } from "../../../../../assets/Icons";

const defaultAnimationOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
};

interface UserDocumentsModalProps {
  visible: boolean;
  onClose: () => void;
}

type DocumentKey = "pan" | "aadhaar" | "driving" | "passport" | "voter";

type UploadState = {
  front?: File;
  back?: File;
  meta?: Record<string, string>;
};

const documentConfig: Record<
  DocumentKey,
  {
    title: string;
    subtitle: string;
    helper?: string;
    docType: string;
    backDocType?: string;
    requiresBack?: boolean;
    mandatory?: boolean;
    inputLabels?: {
      id: string;
      label: string;
      placeholder: string;
      type?: "text" | "date";
    }[];
  }
> = {
  pan: {
    title: "Pan Card",
    subtitle: "Verify PAN Card",
    helper: "Upload PAN",
    docType: "PAN_CARD",
    inputLabels: [
      { id: "panNumber", label: "PAN Card No", placeholder: "ABCDE1234F" },
      {
        id: "panDob",
        label: "DOB as per PAN",
        placeholder: "dd/mm/yyyy",
        type: "date",
      },
    ],
    mandatory: true,
  },
  aadhaar: {
    title: "Aadhaar Card",
    subtitle: "Verify Aadhaar Card",
    helper: "Upload Aadhaar Card",
    docType: "AADHAR_CARD",
    backDocType: "AADHAR_CARD_BACK",
    requiresBack: true,
    inputLabels: [
      { id: "aadhaarNumber", label: "Aadhaar Card No", placeholder: "XXXX XXXX XXXX" },
    ],
  },
  driving: {
    title: "Driving License",
    subtitle: "Verify Driving License",
    helper: "Upload Driving License",
    docType: "DRIVING_LICENCE",
    backDocType: "DRIVING_LICENCE_BACK",
    requiresBack: true,
    inputLabels: [
      { id: "drivingNumber", label: "Driving license no.", placeholder: "XX00 000000" },
      {
        id: "drivingDob",
        label: "DOB as per DL",
        placeholder: "dd/mm/yyyy",
        type: "date",
      },
    ],
  },
  passport: {
    title: "Indian Passport",
    subtitle: "Verify Indian Passport",
    helper: "Upload Passport",
    docType: "PASSPORT_FRONT",
    backDocType: "PASSPORT_BACK",
    requiresBack: true,
    inputLabels: [
      { id: "passportNumber", label: "Passport no.", placeholder: "A1234567" },
      {
        id: "passportDob",
        label: "DOB as per passport",
        placeholder: "dd/mm/yyyy",
        type: "date",
      },
      { id: "passportFileNumber", label: "Passport file no.", placeholder: "" },
    ],
  },
  voter: {
    title: "Voter ID",
    subtitle: "Verify Voter ID",
    helper: "Upload Voter ID",
    docType: "VOTER_CARD",
    backDocType: "VOTER_CARD_BACK",
    requiresBack: true,
    inputLabels: [{ id: "voterNumber", label: "Voter Card ID", placeholder: "ABC1234567" }],
  },
};

const getDocTitleByType = (docType: string) => {
  const matchingEntry = Object.values(documentConfig).find(
    (config) => config.docType === docType || config.backDocType === docType,
  );
  return matchingEntry?.title ?? docType;
};

const formatFileLabel = (file?: File) => {
  // if (!file) return "Drag & drop or click to upload";
  if (!file) return "click to upload";
  return `${file.name} • ${(file.size / 1024).toFixed(1)} KB`;
};

const formatDateInputValue = (value?: string) => {
  if (!value) return "";
  const parsed = moment(value, ["DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
};

export const UserDocumentsModal: React.FC<UserDocumentsModalProps> = ({
  visible,
  onClose,
}) => {

  const dispatch = useAppDispatch();

  const { remitter } = useAppSelector((state) => state.remittance);
  const { documentUploads, documentVerification } = useAppSelector(
    (state) => state.remittance.remitter.remitterDocuments,
  );
  const userDocuments = useMemo<any[]>(
    () =>
      remitter?.remitterDocuments?.userDocuments?.data ??
      remitter?.remitterDocuments?.userDocuments?.list ??
      [],
    [remitter?.remitterDocuments?.userDocuments],
  );
  
  const [activeDocument, setActiveDocument] = useState<DocumentKey>("pan");
  const [uploads, setUploads] = useState<Partial<Record<DocumentKey, UploadState>>>(
    {}
  );
  const [inputs, setInputs] = useState<Record<string, string>>({});

  const panVerificationState = documentVerification?.PAN_CARD;
  const isPanVerified = Boolean(panVerificationState?.isVerified);

  const remitterDob = useMemo(() => {
    const dob = remitter?.selectedRemitter?.dob;
    if (!dob) return "";
    const parsed = moment(dob, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"], true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  }, [remitter?.selectedRemitter?.dob]);
  
  useEffect(() => {
    setActiveDocument("pan");
  }, [visible])

  useEffect(() => {
    if (!visible) return;

    const panDoc = userDocuments.find((doc: any) => doc?.docName?.includes("PAN_CARD"));

    if (panDoc) {
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType: "PAN_CARD",
          side: "front",
          data: panDoc,
          loading: false,
          error: null,
        }),
      );

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType: "PAN_CARD",
          data: {
            ...panDoc,
            aadhaarLinked: true,
          },
          loading: false,
          error: null,
          isVerified: Boolean(panDoc?.isVerified),
        }),
      );

      setInputs((prev) => ({
        ...prev,
        panNumber: panDoc?.idNumber ?? prev.panNumber ?? "",
        panDob: panDoc?.doc_dob ?? remitterDob ?? prev.panDob ?? "",
      }));

      setUploads((prev) => ({
        ...prev,
        pan: {
          ...prev.pan,
          meta: {
            ...prev.pan?.meta,
            frontLabel: panDoc?.docName ?? "PAN uploaded",
          },
          front: prev.pan?.front,
        },
      }));
    }

    const aadhaarDoc = userDocuments.find(
      (doc: any) => doc?.doc_type === "AADHAR_CARD" || doc?.docName?.includes("AADHAR_CARD"),
    );

    if (aadhaarDoc) {
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType: "AADHAR_CARD",
          side: "front",
          data: aadhaarDoc,
          loading: false,
          error: null,
        }),
      );

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType: "AADHAR_CARD",
          data: aadhaarDoc,
          loading: false,
          error: null,
          isVerified: Boolean(aadhaarDoc?.isVerified),
        }),
      );

      setInputs((prev) => ({
        ...prev,
        aadhaarNumber: aadhaarDoc?.idNumber ?? prev.aadhaarNumber ?? "",
        aadhaarName: aadhaarDoc?.nameOnCard ?? prev.aadhaarName ?? "",
      }));

      setUploads((prev) => ({
        ...prev,
        aadhaar: {
          ...prev.aadhaar,
          meta: {
            ...prev.aadhaar?.meta,
            frontLabel: aadhaarDoc?.docName ?? "Aadhaar uploaded",
          },
          front: prev.aadhaar?.front,
        },
      }));
    }

    const drivingDoc = userDocuments.find(
      (doc: any) => doc?.doc_type === "DRIVING_LICENCE" || doc?.docName?.includes("DRIVING_LICENCE"),
    );

    if (drivingDoc) {
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType: "DRIVING_LICENCE",
          side: "front",
          data: drivingDoc,
          loading: false,
          error: null,
        }),
      );

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType: "DRIVING_LICENCE",
          data: drivingDoc,
          loading: false,
          error: null,
          isVerified: Boolean(drivingDoc?.isVerified),
        }),
      );

      setInputs((prev) => ({
        ...prev,
        drivingNumber: drivingDoc?.idNumber ?? prev.drivingNumber ?? "",
        drivingDob: drivingDoc?.doc_dob ?? prev.drivingDob ?? "",
      }));

      setUploads((prev) => ({
        ...prev,
        driving: {
          ...prev.driving,
          meta: {
            ...prev.driving?.meta,
            frontLabel: drivingDoc?.docName ?? "Driving Licence uploaded",
          },
          front: prev.driving?.front,
        },
      }));
    }

    const passportFrontDoc = userDocuments.find(
      (doc: any) =>
        doc?.doc_type === "PASSPORT_FRONT" || doc?.docName?.includes("PASSPORT_FRONT"),
    );

    if (passportFrontDoc) {
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType: "PASSPORT_FRONT",
          side: "front",
          data: passportFrontDoc,
          loading: false,
          error: null,
        }),
      );

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType: "PASSPORT_FRONT",
          data: passportFrontDoc,
          loading: false,
          error: null,
          isVerified: Boolean(passportFrontDoc?.isVerified),
        }),
      );

      setInputs((prev) => ({
        ...prev,
        passportNumber: passportFrontDoc?.idNumber ?? prev.passportNumber ?? "",
        passportDob: passportFrontDoc?.doc_dob ?? prev.passportDob ?? "",
      }));

      setUploads((prev) => ({
        ...prev,
        passport: {
          ...prev.passport,
          meta: {
            ...prev.passport?.meta,
            frontLabel: passportFrontDoc?.docName ?? "Passport front uploaded",
          },
          front: prev.passport?.front,
        },
      }));
    }

    const passportBackDoc = userDocuments.find(
      (doc: any) => doc?.doc_type === "PASSPORT_BACK" || doc?.docName?.includes("PASSPORT_BACK"),
    );

    if (passportBackDoc) {
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType: "PASSPORT_BACK",
          side: "back",
          data: passportBackDoc,
          loading: false,
          error: null,
        }),
      );

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType: "PASSPORT_BACK",
          data: passportBackDoc,
          loading: false,
          error: null,
          isVerified: Boolean(passportBackDoc?.isVerified),
        }),
      );

      setInputs((prev) => ({
        ...prev,
        passportNumber: passportBackDoc?.idNumber ?? prev.passportNumber ?? "",
        passportDob: passportBackDoc?.doc_dob ?? prev.passportDob ?? "",
        passportFileNumber: passportBackDoc?.file_number ?? prev.passportFileNumber ?? "",
      }));

      setUploads((prev) => ({
        ...prev,
        passport: {
          ...prev.passport,
          meta: {
            ...prev.passport?.meta,
            backLabel: passportBackDoc?.docName ?? "Passport back uploaded",
          },
          back: prev.passport?.back,
        },
      }));
    }

    const voterDoc = userDocuments.find(
      (doc: any) => doc?.doc_type === "VOTER_CARD" || doc?.docName?.includes("VOTER_CARD"),
    );

    if (voterDoc) {
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType: "VOTER_CARD",
          side: "front",
          data: voterDoc,
          loading: false,
          error: null,
        }),
      );

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType: "VOTER_CARD",
          data: voterDoc,
          loading: false,
          error: null,
          isVerified: Boolean(voterDoc?.isVerified),
        }),
      );

      setInputs((prev) => ({
        ...prev,
        voterNumber: voterDoc?.idNumber ?? prev.voterNumber ?? "",
        voterDob: voterDoc?.doc_dob || remitterDob || prev.voterDob || "",
      }));

      setUploads((prev) => ({
        ...prev,
        voter: {
          ...prev.voter,
          meta: {
            ...prev.voter?.meta,
            frontLabel: voterDoc?.docName ?? "Voter ID uploaded",
          },
          front: prev.voter?.front,
        },
      }));
    }
  }, [dispatch, remitterDob, userDocuments, visible]);

  useEffect(() => {
    if (!isPanVerified) return;

    const isAadhaarLinked = panVerificationState?.data?.aadhaarLinked;
    if (isAadhaarLinked === false) return;

    setActiveDocument("aadhaar");

  }, [isPanVerified, panVerificationState?.data?.aadhaarLinked]);

  const isUploading = useMemo(() => {
    return Object.values(documentUploads ?? {}).some(
      (uploadState) => uploadState?.front?.loading || uploadState?.back?.loading,
    );
  }, [documentUploads]);

  const isVerifying = useMemo(() => {
    return Object.values(documentVerification ?? {}).some(
      (verificationState) => verificationState?.loading,
    );
  }, [documentVerification]);

  const showLoader = isUploading || isVerifying;

  const getUploadState = (docType?: string, side?: "front" | "back") => {
    if (!docType || !side) return undefined;
    return documentUploads?.[docType]?.[side];
  };

  const otherDocsVerified = useMemo(() => {
    const aadhaarVerified = Boolean(documentVerification?.AADHAR_CARD?.isVerified);
    const drivingVerified = Boolean(documentVerification?.DRIVING_LICENCE?.isVerified);
    const passportVerified = Boolean(documentVerification?.PASSPORT_BACK?.isVerified);
    const voterVerified = Boolean(documentVerification?.VOTER_CARD?.isVerified);

    return aadhaarVerified || drivingVerified || passportVerified || voterVerified;
  }, [documentVerification]);

  const completedCount = useMemo(() => {
    return Number(isPanVerified) + Number(otherDocsVerified);
  }, [isPanVerified, otherDocsVerified]);

  const isSubmitDisabled = !isPanVerified || !otherDocsVerified || showLoader;

  const remitterVerifyClick = () => {
    const configData = documentConfig[activeDocument];
    if (configData.docType === "PAN_CARD") {
      remitterVerifyPAN(configData.docType);
    }
    if (configData.docType === "AADHAR_CARD") {
      remitterVerifyAadhaar(configData.docType);
    }
    if (configData.docType === "DRIVING_LICENCE") {
      remitterVerifyDriving(configData.docType);
    }
    if (configData.docType === "PASSPORT_FRONT") {
      const isPassportFrontVerified = Boolean(documentVerification?.PASSPORT_FRONT?.isVerified);
      if (isPassportFrontVerified && configData.backDocType) {
        remitterVerifyPassportBack(configData.backDocType);
      } else {
        remitterVerifyPassportFront(configData.docType);
      }
    }
    if (configData.docType === "VOTER_CARD") {
      remitterVerifyVoter(configData.docType);
    }
  };

  const remitterVerifyPAN = (docType: string) => {
    const panFile = uploads?.pan?.front;
    if (!panFile) {
      toast.error("Please upload a PAN card file");
      return;
    }

    dispatch(
      remittanceActions.setDocumentVerificationState({
        docType,
        loading: true,
        error: null,
        isVerified: false,
      }),
    );

    const data = new FormData();
    data.append("file", panFile);
    data.append("doc_type", docType);
    const panFrontUpload = documentUploads?.[docType]?.front?.data;
    if (panFrontUpload?.idNumber) {
      data.append("id_number", panFrontUpload.idNumber);
    }
    data.append("edited_id_number", inputs.panNumber);
    if (panFrontUpload?.nameOnCard) {
      data.append("name_on_card", panFrontUpload.nameOnCard);
    }
    if (panFrontUpload?.request_id) {
      data.append("request_id", panFrontUpload.request_id);
    }
    if (inputs.panDob) {
      data.append("doc_dob", moment(inputs.panDob, "YYYY-MM-DD ").format("DD/MM/YYYY"));
    }
    remittanceApi.remitterVerifyPAN(remitter?.selectedRemitter?.userKey, data)
      .then((response) => {
        const verificationData = {
          ...(response?.data ?? {}),
          aadhaarLinked: Boolean(response?.data?.aadhaarLinked === true)
        };
        dispatch( 
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            data: verificationData,
            error: null,
            isVerified: true,
          }),
        );
        if(response?.data?.aadhaarLinked) {
          toast.success(`${getDocTitleByType(docType)} verification successful`);
        }
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        logApiErrorToSentry(err, "remittanceApi.uploadRemitterDocuments");
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            error: errorMsg,
            data: null,
            isVerified: false,
          }),
        );
      })
  }

  const remitterVerifyAadhaar = (docType: string) => {
    const aadhaarFile = uploads?.aadhaar?.front;
    const aadhaarBackUploaded = Boolean(
      uploads?.aadhaar?.back || documentUploads?.AADHAR_CARD_BACK?.back?.data,
    );
    if (!aadhaarFile) {
      toast.error("Please upload Aadhaar front side");
      return;
    }

    if (documentConfig.aadhaar.requiresBack && !aadhaarBackUploaded) {
      toast.error("Please upload Aadhaar back side");
      return;
    }

    dispatch(
      remittanceActions.setDocumentVerificationState({
        docType,
        loading: true,
        error: null,
        isVerified: false,
      }),
    );

    const data = new FormData();
    data.append("file", aadhaarFile);
    data.append("doc_type", docType);

    const aadhaarFrontUpload = documentUploads?.[docType]?.front?.data;
    const aadhaarNumber = aadhaarFrontUpload?.idNumber || inputs.aadhaarNumber;
    const aadhaarName = aadhaarFrontUpload?.nameOnCard || inputs.aadhaarName;

    if (aadhaarNumber) {
      data.append("id_number", aadhaarNumber);
    }

    if (aadhaarName) {
      data.append("name_on_card", aadhaarName);
    }

    if (aadhaarFrontUpload?.request_id) {
      data.append("request_id", aadhaarFrontUpload.request_id);
    }

    remittanceApi.remitterVerifyAadhar(remitter?.selectedRemitter?.userKey, data)
      .then((response) => {
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            data: response?.data ?? null,
            error: null,
            isVerified: true,
          }),
        );
        toast.success(`${getDocTitleByType(docType)} verification successful`);
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        logApiErrorToSentry(err, "remittanceApi.remitterVerifyAadhar");
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            error: errorMsg,
            data: null,
            isVerified: false,
          }),
        );
      });
  }

  const remitterVerifyPassportFront = (docType: string) => {
    const passportFrontFile = uploads?.passport?.front;
    if (!passportFrontFile) {
      toast.error("Please upload Passport front side");
      return;
    }

    dispatch(
      remittanceActions.setDocumentVerificationState({
        docType,
        loading: true,
        error: null,
        isVerified: false,
      }),
    );

    const passportFrontUpload = documentUploads?.[docType]?.front?.data;
    const passportNumber = inputs.passportNumber || passportFrontUpload?.idNumber;
    const passportDob = inputs.passportDob || passportFrontUpload?.doc_dob;
    const requestId = passportFrontUpload?.request_id;
    const formattedDob = passportDob
      ? moment(passportDob, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"]).format("DD/MM/YYYY")
      : null;

    const data = new FormData();
    data.append("file", passportFrontFile);
    data.append("doc_type", docType);

    if (passportNumber) {
      data.append("id_number", passportNumber);
    }

    if (passportFrontUpload?.nameOnCard) {
      data.append("name_on_card", passportFrontUpload.nameOnCard);
    }

    if (requestId) {
      data.append("request_id", requestId);
    }

    if (formattedDob) {
      data.append("doc_dob", formattedDob);
    }

    remittanceApi.remitterVerifyPassportFront(remitter?.selectedRemitter?.userKey, data)
      .then((response) => {
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            data: response?.data ?? null,
            error: null,
            isVerified: true,
          }),
        );
        toast.success(`${getDocTitleByType(docType)} front verification successful`);
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        logApiErrorToSentry(err, "remittanceApi.remitterVerifyPassportFront");
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            error: errorMsg,
            data: null,
            isVerified: false,
          }),
        );
      });
  };

  const remitterVerifyPassportBack = (docType: string) => {
    const passportBackFile = uploads?.passport?.back;
    const passportFrontUpload = documentUploads?.PASSPORT_FRONT?.front?.data;
    const passportBackUpload = documentUploads?.PASSPORT_BACK?.back?.data;

    if (!documentVerification?.PASSPORT_FRONT?.isVerified) {
      toast.error("Please verify Passport front side first");
      return;
    }

    if (!passportBackFile) {
      toast.error("Please upload Passport back side");
      return;
    }

    dispatch(
      remittanceActions.setDocumentVerificationState({
        docType,
        loading: true,
        error: null,
        isVerified: false,
      }),
    );

    const passportNumber =
      passportFrontUpload?.idNumber || passportBackUpload?.idNumber || inputs.passportNumber;
    const fileNumber = passportBackUpload?.file_number || inputs.passportFileNumber;
    const passportDob = inputs.passportDob || passportBackUpload?.doc_dob || passportFrontUpload?.doc_dob;
    const formattedDob = passportDob
      ? moment(passportDob, ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"]).format("DD/MM/YYYY")
      : null;
    const requestId = passportBackUpload?.request_id || passportFrontUpload?.request_id;

    const data = new FormData();
    data.append("file", passportBackFile);
    data.append("doc_type", docType);

    if (passportNumber) {
      data.append("id_number", passportNumber);
    }

    if (fileNumber) {
      data.append("file_number", fileNumber);
    }

    if (passportBackUpload?.pincode) {
      data.append("pincode", passportBackUpload.pincode);
    }

    if (passportBackUpload?.address) {
      data.append("address", passportBackUpload.address);
    }

    if (passportBackUpload?.city) {
      data.append("city", passportBackUpload.city);
    }

    if (passportBackUpload?.state) {
      data.append("state", passportBackUpload.state);
    }

    if (formattedDob) {
      data.append("doc_dob", formattedDob);
    }

    if (requestId) {
      data.append("request_id", requestId);
    }

    remittanceApi.remitterVerifyPassportBack(remitter?.selectedRemitter?.userKey, data)
      .then((response) => {
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            data: response?.data ?? null,
            error: null,
            isVerified: true,
          }),
        );
        toast.success(`${getDocTitleByType(docType)} verification successful`);
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        toast.error(errorMsg);
        logApiErrorToSentry(err, "remittanceApi.remitterVerifyPassportBack");
        dispatch(
          remittanceActions.setDocumentVerificationState({
            docType,
            loading: false,
            error: errorMsg,
            data: null,
            isVerified: false,
          }),
        );
      });
  };

  const remitterVerifyDriving = async (docType: string) => {
    const drivingFile = uploads?.driving?.front;
    const drivingBackFile = uploads?.driving?.back;
    const drivingBackUploaded = Boolean(drivingBackFile || documentUploads?.DRIVING_LICENCE?.back?.data);
    const drivingFrontDocType = documentConfig.driving.docType ?? docType;
    const drivingBackDocType = documentConfig.driving.backDocType ?? documentConfig.driving.docType ?? docType;

    if (!drivingFile) {
      toast.error("Please upload Driving License front side");
      return;
    }

    if (documentConfig.driving.requiresBack && !drivingBackUploaded) {
      toast.error("Please upload Driving License back side");
      return;
    }

    dispatch(
      remittanceActions.setDocumentVerificationState({
        docType,
        loading: true,
        error: null,
        isVerified: false,
      }),
    );

    const drivingFrontUpload = documentUploads?.[docType]?.front?.data;
    const drivingIdNumber = drivingFrontUpload?.idNumber || inputs.drivingNumber;
    const drivingDob = drivingFrontUpload?.doc_dob || inputs.drivingDob;

    const buildDrivingFormData = async (
      docMeta?: { doc_name?: string; id?: string | number },
      options?: {
        includeEditedId?: boolean;
        includeIdNumber?: boolean;
        idNumberKey?: "id_number" | "idNumber";
        docTypeOverride?: string;
        attachment?: File;
      },
    ) => {
      const data = new FormData();
      const drivingAttachment = options?.attachment ?? drivingFile;

      const includeEditedId = options?.includeEditedId ?? true;
      const includeIdNumber = options?.includeIdNumber ?? true;
      const idNumberKey = options?.idNumberKey ?? "id_number";

      if (drivingAttachment instanceof File) {
        data.append("file", drivingAttachment);
      } else {
        throw new Error("Driving licence attachment missing");
      }

      data.append("doc_type", options?.docTypeOverride ?? drivingFrontDocType);

      if (includeIdNumber && drivingIdNumber) {
        data.append(idNumberKey, drivingIdNumber);
      }

      if (includeEditedId && inputs.drivingNumber) {
        data.append("edited_id_number", inputs.drivingNumber);
      }

      if (drivingFrontUpload?.nameOnCard) {
        data.append("name_on_card", drivingFrontUpload.nameOnCard);
      }

      if (drivingFrontUpload?.request_id) {
        data.append("request_id", drivingFrontUpload.request_id);
      }

      if (drivingDob) {
        data.append("doc_dob", drivingDob);
      }

      if (docMeta && docMeta.doc_name !== undefined) {
        data.append("doc_name", docMeta.doc_name);
      }

      if (docMeta && docMeta.id !== undefined) {
        data.append("id", `${docMeta.id}`);
      }

      return data;
    };

    try {
      const verificationFormData = await buildDrivingFormData(undefined, {
        docTypeOverride: drivingFrontDocType,
        attachment: drivingFile,
      });
      const response = await remittanceApi.remitterVerifyDL(remitter?.selectedRemitter?.userKey, verificationFormData);

      const docName = response?.data?.result?.docName;
      const docId = response?.data?.result?.id;

      if (docName && docId) {
        const documentVerificationData = await buildDrivingFormData(
          {
            doc_name: docName,
            id: docId,
          },
          {
            includeEditedId: false,
            includeIdNumber: true,
            idNumberKey: "idNumber",
            docTypeOverride: drivingBackDocType,
            attachment: drivingBackFile,
          },
        );
        await remittanceApi.remitterVerifyDLLDocument(
          remitter?.selectedRemitter?.userKey,
          documentVerificationData,
        );
      } else {
        throw new Error("Missing driving licence document reference");
      }

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType,
          loading: false,
          data: response?.data ?? null,
          error: null,
          isVerified: true,
        }),
      );
      toast.success(`${getDocTitleByType(docType)} verification successful`);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error?.type ||
        err?.response?.error?.type ||
        err?.response?.error ||
        err?.message ||
        AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      logApiErrorToSentry(err, "remittanceApi.remitterVerifyDL");
      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType,
          loading: false,
          error: errorMsg,
          data: null,
          isVerified: false,
        }),
      );
    }
  };

  const remitterVerifyVoter = async (docType: string) => {
    const voterFile = uploads?.voter?.front;
    const voterBackFile = uploads?.voter?.back;
    const voterBackUploaded = Boolean(voterBackFile || documentUploads?.VOTER_CARD?.back?.data);
    const voterFrontDocType = documentConfig.voter.docType ?? docType;
    const voterBackDocType = documentConfig.voter.backDocType ?? documentConfig.voter.docType ?? docType;

    if (!voterFile) {
      toast.error("Please upload Voter ID front side");
      return;
    }

    if (documentConfig.voter.requiresBack && !voterBackUploaded) {
      toast.error("Please upload Voter ID back side");
      return;
    }

    dispatch(
      remittanceActions.setDocumentVerificationState({
        docType,
        loading: true,
        error: null,
        isVerified: false,
      }),
    );

    const voterFrontUpload = documentUploads?.[docType]?.front?.data;
    const voterIdNumber = voterFrontUpload?.idNumber || inputs.voterNumber;
    const voterDob =
      voterFrontUpload?.doc_dob ||
      inputs.voterDob ||
      remitterDob ||
      documentUploads?.PAN_CARD?.front?.data?.doc_dob ||
      inputs.panDob;

    const buildVoterFormData = async (
      docMeta?: { doc_name?: string; id?: string | number },
      options?: {
        includeEditedId?: boolean;
        includeIdNumber?: boolean;
        idNumberKey?: "id_number" | "idNumber";
        docTypeOverride?: string;
        attachment?: File;
      },
    ) => {
      const data = new FormData();
      const voterAttachment = options?.attachment ?? voterFile;

      const includeEditedId = options?.includeEditedId ?? true;
      const includeIdNumber = options?.includeIdNumber ?? true;
      const idNumberKey = options?.idNumberKey ?? "id_number";

      if (voterAttachment instanceof File) {
        data.append("file", voterAttachment);
      } else {
        throw new Error("Voter ID attachment missing");
      }

      data.append("doc_type", options?.docTypeOverride ?? voterFrontDocType);

      if (includeIdNumber && voterIdNumber) {
        data.append(idNumberKey, voterIdNumber);
      }

      if (includeEditedId && inputs.voterNumber) {
        data.append("edited_id_number", inputs.voterNumber);
      }

      if (voterFrontUpload?.nameOnCard) {
        data.append("name_on_card", voterFrontUpload.nameOnCard);
      }

      if (voterFrontUpload?.request_id) {
        data.append("request_id", voterFrontUpload.request_id);
      }

      if (voterDob) {
        data.append("doc_dob", voterDob);
      }

      if (docMeta && docMeta.doc_name !== undefined) {
        data.append("doc_name", docMeta.doc_name);
      }

      if (docMeta && docMeta.id !== undefined) {
        data.append("id", `${docMeta.id}`);
      }

      return data;
    };

    try {
      const verificationFormData = await buildVoterFormData(undefined, {
        docTypeOverride: voterFrontDocType,
        attachment: voterFile,
      });
      const response = await remittanceApi.remitterVerifyVoterID(
        remitter?.selectedRemitter?.userKey,
        verificationFormData,
      );

      const docName = response?.data?.result?.docName;
      const docId = response?.data?.result?.id;

      if (docName && docId) {
        const documentVerificationData = await buildVoterFormData(
          { doc_name: docName, id: docId },
          {
            includeEditedId: false,
            includeIdNumber: true,
            idNumberKey: "idNumber",
            docTypeOverride: voterBackDocType,
            attachment: voterBackFile,
          },
        );
        await remittanceApi.remitterVerifyVoterIDLDocument(
          remitter?.selectedRemitter?.userKey,
          documentVerificationData,
        );
      } else {
        throw new Error("Missing voter ID document reference");
      }

      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType,
          loading: false,
          data: response?.data ?? null,
          error: null,
          isVerified: true,
        }),
      );
      toast.success(`${getDocTitleByType(docType)} verification successful`);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error?.type ||
        err?.response?.error?.type ||
        err?.response?.error ||
        err?.message ||
        AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      logApiErrorToSentry(err, "remittanceApi.remitterVerifyVoterID");
      dispatch(
        remittanceActions.setDocumentVerificationState({
          docType,
          loading: false,
          error: errorMsg,
          data: null,
          isVerified: false,
        }),
      );
    }
  };

  const handleDocumentSelect = (key: DocumentKey) => {
    if (key !== "pan" && !isPanVerified) {
      return;
    }
    setActiveDocument(key);
  };

  const handleFileChange = (key: DocumentKey, side: "front" | "back", file?: File) => {
    const configData = documentConfig[key];
    const isLocalOnlyBackUpload =
      side === "back" && (key === "driving" || key === "voter");

    if (file) {
      if (isLocalOnlyBackUpload) {
        setUploads((prev) => ({
          ...prev,
          [key]: { ...prev[key], [side]: file },
        }));
        return;
      }

      const data = new FormData();
      const docType = side === "front" ? configData?.docType : configData?.backDocType;
      if (!docType) {
        toast.error("Document type is missing");
        return;
      }
      data.append("file", file);
      if (!(key === "aadhaar" && side === "back")) {
        data.append("doc_type", docType);
      }
      dispatch(
        remittanceActions.setDocumentUploadState({
          docType,
          side,
          loading: true,
          error: null,
        }),
      );
      const uploadPromise =
        key === "aadhaar" && side === "back"
          ? remittanceApi.uploadRemitterAadharBack(remitter?.selectedRemitter?.userKey, data)
          : remittanceApi.uploadRemitterDocuments(remitter?.selectedRemitter?.userKey, data);

      uploadPromise
        .then((response) => {
          const nextInputs = { ...inputs };
          if (docType === "PAN_CARD") {
            nextInputs.panNumber = response?.data?.idNumber ?? "";
            nextInputs.panDob = response?.data?.doc_dob ?? "";
            dispatch(
              remittanceActions.setDocumentVerificationState({
                docType,
                isVerified: false,
              }),
            );
          }

          if (docType === "AADHAR_CARD") {
            nextInputs.aadhaarNumber = response?.data?.idNumber ?? "";
            nextInputs.aadhaarName = response?.data?.nameOnCard ?? "";
            dispatch(
              remittanceActions.setDocumentVerificationState({
                docType,
                isVerified: false,
              }),
            );
          }

          if (docType === "DRIVING_LICENCE") {
            nextInputs.drivingNumber = response?.data?.idNumber ?? "";
            nextInputs.drivingDob = response?.data?.doc_dob ?? "";
            dispatch(
              remittanceActions.setDocumentVerificationState({
                docType,
                isVerified: false,
              }),
            );
          }

          if (docType === "VOTER_CARD") {
            nextInputs.voterNumber = response?.data?.idNumber ?? "";
            nextInputs.voterDob = response?.data?.doc_dob ?? remitterDob ?? "";
            dispatch(
              remittanceActions.setDocumentVerificationState({
                docType,
                isVerified: false,
              }),
            );
          }

          if (docType === "PASSPORT_FRONT") {
            nextInputs.passportNumber = response?.data?.idNumber ?? "";
            nextInputs.passportDob = response?.data?.doc_dob ?? "";
            dispatch(
              remittanceActions.setDocumentVerificationState({
                docType,
                isVerified: false,
              }),
            );
          }

          if (docType === "PASSPORT_BACK") {
            nextInputs.passportFileNumber = response?.data?.file_number ?? "";
            nextInputs.passportNumber = response?.data?.idNumber ?? nextInputs.passportNumber ?? "";
            nextInputs.passportDob = response?.data?.doc_dob ?? nextInputs.passportDob ?? "";
            dispatch(
              remittanceActions.setDocumentVerificationState({
                docType,
                isVerified: false,
              }),
            );
          }
          setInputs(nextInputs);
          dispatch(
            remittanceActions.setDocumentUploadState({
              docType,
              side,
              loading: false,
              data: response?.data ?? null,
              error: null,
            }),
          );
          setUploads((prev) => ({
            ...prev,
            [key]: { ...prev[key], [side]: file },
          }));
        })
        .catch((err) => {
          const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
          toast.error(errorMsg);
          logApiErrorToSentry(err, "remittanceApi.uploadRemitterDocuments");
          if (docType === "PAN_CARD") {
            setInputs((prev) => ({
              ...prev,
              panNumber: "",
              panDob: "",
            }));
          }
          dispatch(
            remittanceActions.setDocumentUploadState({
              docType,
              side,
              loading: false,
              data: null,
              error: errorMsg,
            }),
          );
          setUploads((prev) => ({
            ...prev,
            [key]: { ...prev[key], [side]: null },
          }));
        })
    }
  };

  const activeConfig = documentConfig[activeDocument];
  const isPanDocument = activeDocument === "pan";
  const isAadhaarDocument = activeDocument === "aadhaar";
  const isDrivingDocument = activeDocument === "driving";
  const isPassportDocument = activeDocument === "passport";
  const isVoterDocument = activeDocument === "voter";
  const panFile = uploads.pan?.front;
  const panUploadLoading = getUploadState("PAN_CARD", "front")?.loading;
  const aadhaarFile = uploads.aadhaar?.front;
  const aadhaarBackState = getUploadState("AADHAR_CARD_BACK", "back");
  const aadhaarFrontUploadState = getUploadState("AADHAR_CARD", "front");
  const aadhaarVerificationState = documentVerification?.AADHAR_CARD;
  const isAadhaarVerified = Boolean(aadhaarVerificationState?.isVerified);
  const drivingFile = uploads.driving?.front;
  const drivingUploadState = getUploadState("DRIVING_LICENCE", "front");
  const drivingBackState = getUploadState("DRIVING_LICENCE", "back");
  const drivingVerificationState = documentVerification?.DRIVING_LICENCE;
  const isDrivingVerified = Boolean(drivingVerificationState?.isVerified);
  const passportFrontFile = uploads.passport?.front;
  const passportBackFile = uploads.passport?.back;
  const passportFrontUploadState = getUploadState("PASSPORT_FRONT", "front");
  const passportBackUploadState = getUploadState("PASSPORT_BACK", "back");
  const passportFrontVerificationState = documentVerification?.PASSPORT_FRONT;
  const passportBackVerificationState = documentVerification?.PASSPORT_BACK;
  const isPassportFrontVerified = Boolean(passportFrontVerificationState?.isVerified);
  const isPassportBackVerified = Boolean(passportBackVerificationState?.isVerified);
  const passportFrontUploaded = Boolean(passportFrontFile || passportFrontUploadState?.data);
  const voterFile = uploads.voter?.front;
  const voterUploadState = getUploadState("VOTER_CARD", "front");
  const voterBackState = getUploadState("VOTER_CARD", "back");
  const voterVerificationState = documentVerification?.VOTER_CARD;
  const isVoterVerified = Boolean(voterVerificationState?.isVerified);
  const activeVerificationState = isPanDocument
    ? panVerificationState
    : isAadhaarDocument
      ? aadhaarVerificationState
      : isDrivingDocument
        ? drivingVerificationState
        : isPassportDocument
          ? isPassportFrontVerified
            ? passportBackVerificationState
            : passportFrontVerificationState
          : voterVerificationState;
  const isVerifyButtonVisible =
    (isPanDocument && !isPanVerified) ||
    (isAadhaarDocument && !isAadhaarVerified) ||
    (isDrivingDocument && !isDrivingVerified) ||
    (isPassportDocument && !isPassportBackVerified) ||
    (isVoterDocument && !isVoterVerified);
  const verifyButtonLabel = isPassportDocument
    ? isPassportFrontVerified
      ? "Verify Passport Back"
      : "Verify Passport Front"
    : "Verify";
  const isVerifyDisabled = isPanDocument
    ? !panFile || !inputs.panNumber?.trim() || !inputs.panDob?.trim() || !!panUploadLoading || panVerificationState?.loading
    : isAadhaarDocument
      ? !aadhaarFile ||
        !inputs.aadhaarNumber?.trim() ||
        aadhaarVerificationState?.loading ||
        aadhaarFrontUploadState?.loading ||
        aadhaarBackState?.loading ||
        (activeConfig.requiresBack && !aadhaarBackState?.data && !uploads.aadhaar?.back)
      : isDrivingDocument
        ? !drivingFile ||
          !inputs.drivingNumber?.trim() ||
          drivingVerificationState?.loading ||
          drivingUploadState?.loading ||
          (activeConfig.requiresBack && !drivingBackState?.data && !uploads.driving?.back)
        : isPassportDocument
          ? isPassportFrontVerified
            ? !passportBackFile ||
              !inputs.passportNumber?.trim() ||
              !inputs.passportFileNumber?.trim() ||
              passportBackVerificationState?.loading ||
              passportBackUploadState?.loading
            : !passportFrontFile ||
              !inputs.passportNumber?.trim() ||
              !inputs.passportDob?.trim() ||
              passportFrontVerificationState?.loading ||
              passportFrontUploadState?.loading
          : isVoterDocument
            ? !voterFile ||
              !inputs.voterNumber?.trim() ||
              voterVerificationState?.loading ||
              voterUploadState?.loading ||
              (activeConfig.requiresBack && !voterBackState?.data && !uploads.voter?.back)
            : true;

  const aadhaarLinked = panVerificationState?.data?.aadhaarLinked;
  const showAadhaarLinkWarning = isPanDocument && isPanVerified && !aadhaarLinked;

  const renderUploadCard = (label: string, side: "front" | "back") => {
    const file = uploads[activeDocument]?.[side];
    const docType = side === "front" ? activeConfig?.docType : activeConfig?.backDocType;
    const uploadState = getUploadState(docType, side);
    const metaLabel = uploads[activeDocument]?.meta?.[`${side}Label`];
    const isUploaded = Boolean(file || uploadState?.data || metaLabel);
    const isPassportBackUploadBlocked =
      activeDocument === "passport" && side === "back" && !isPassportFrontVerified;
    const isUploadDisabled =
      (activeDocument === "pan" && isPanVerified) ||
      (activeDocument === "aadhaar" && isAadhaarVerified) ||
      (activeDocument === "driving" && isDrivingVerified) ||
      (activeDocument === "passport" &&
        ((side === "front" && isPassportFrontVerified) || (side === "back" && isPassportBackVerified))) ||
      (activeDocument === "voter" && isVoterVerified) ||
      isPassportBackUploadBlocked;
    const helperText = isPassportBackUploadBlocked
      ? "Verify passport front side first"
      : uploadState?.loading
        ? "Uploading..."
        : uploadState?.error || metaLabel || uploadState?.data?.docName || formatFileLabel(file);
    return (
      <label
        className={clsx(
          styles.uploadCard,
          !isUploadDisabled && "hover:border-[#4E8AFF] hover:bg-[#EEF4FF]",
          isUploaded && styles.uploadCardSuccess,
          isUploadDisabled && "cursor-not-allowed opacity-75",
          isPassportBackUploadBlocked && "pointer-events-none opacity-60",
        )}
      >
        <input
          type="file"
          className="hidden"
          disabled={isUploadDisabled}
          onClick={(event) => {
            // Allow selecting the same file again by resetting the value
            event.currentTarget.value = "";
          }}
          onChange={(event) => handleFileChange(activeDocument, side, event.target.files?.[0])}
        />
        <div className="flex items-center gap-4 w-full">
          <div
            className={clsx(
              "h-12 w-12 rounded-2xl bg-white flex items-center justify-center border",
              isUploaded ? "border-[#22C55E] text-[#16A34A]" : "border-[#D0DBFF] text-[#4E8AFF]",
            )}
          >
            {isUploaded ? (
             isUploadDisabled ? (
                <CheckCircle2 className="h-6 w-6" color="#22C55E" />
              ) : (
                <RefreshCw className="h-6 w-6" color="#4E8AFF" />
              )
            ) : (
              <UploadCloud className="h-6 w-6" color="#4E8AFF" />
            )}
          </div>
          <div className="flex flex-col gap-1 text-left flex-1">
            <div className="text-sm font-semibold text-[#0F172A]">{label == "Upload Upload PAN" ? "Upload PAN" : label}</div>
            <div className="text-xs text-[#64748B]">{helperText}</div>
          </div>
        </div>
      </label>
    );
  };

  const resetState = () => {
    setActiveDocument("pan");
    setUploads({});
    setInputs({});
  };

  return (
    <Modal
      visible={visible}
      closeHandler={() => {
        resetState();
        onClose();
      }}
      header={
        <div className={clsx(styles.header, "flex items-center !pl-0 gap-3 mx-8 !py-3")}>
          <div className="h-10 w-10 rounded-xl bg-[#EAF2FF] flex items-center justify-center text-[#3C7CFF]">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className={styles.headerTitle}>Upload Document for KYC</div>
            <div className={styles.headerSubTitle}>Verify your identity securely</div>
          </div>
        </div>
      }
      closable
      closeButtonIcon={<X className="h-5 w-5 text-[#0F172A] mr-8" />}
      modalClass={
        "bg-card border-none !w-[1150px] max-w-[1200px] lg:!w-[1200px] !p-0 bg-[#F6F9FF] rounded-xl"
      }
      headerClass="!my-0 bg-gradient-to-r from-primary/5 to-transparent"
      maskClosable={false}
    >
      <div className={clsx(styles.headerDivider, "!mx-0 !mb-0")} />

      <div className={clsx(styles.body, "!py-4")}>
        <div className={styles.content}>
          <div className={styles.documentList}>
            <div className="flex flex-col gap-2">
              
              {/* ------------------ PAN (Mandatory) ------------------ */}
              {Object.keys(documentConfig as Record<DocumentKey, any>).map((key, index) => {
                const item = documentConfig[key as DocumentKey];
                const isActive = activeDocument === key;
                const isPan = key === "pan";

                /* PAN CARD BLOCK */
                if (isPan) {
                  return (
                    <Box
                      key={key}
                      onClick={() => handleDocumentSelect(key as DocumentKey)}
                      sx={{
                        position: "relative",
                        borderRadius: "18px",
                        bgcolor: isActive ? "#F5F9FF" : "#FFFFFF",
                        border: isActive ? "2px solid #4E8AFF" : "1px solid #E5ECFF",
                        p: 1.5,
                        // pt: 4,
                        // px: 4,
                        cursor: "pointer",
                        transition: "all 0.25s ease",
                        "&:hover": { backgroundColor: "#EEF5FF" },
                        boxShadow: isActive ? "0px 10px 28px rgba(76,131,255,0.28)" : "none"
                      }}
                    >
                      {/* Mandatory tag */}
                      <Chip
                        label="Mandatory"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: "-12px",
                          left: "22px",
                          bgcolor: "#F59E0B",
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "11px",
                          height: "22px",
                          borderRadius: "12px",
                          px: 1.5
                        }}
                      />

                      {/* Blue Left Highlight */}
                      {isActive && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "18px",
                            bottom: "18px",
                            left: "18px",
                            width: "4px",
                            borderRadius: "4px",
                            bgcolor: "#4E8AFF"
                          }}
                        />)}

                      {/* Title + Subtitle */}
                      <Box sx={{ ml: 4 }}>
                        <Typography fontSize="14px" fontWeight={700} color="#083D99">
                          {item.title} <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <Typography fontSize="12px" mt={0.5} color="#64748B">
                          {item.subtitle}
                        </Typography>
                      </Box>

                      {/* Flag icon right */}
                      <Box
                        sx={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: "#F1F5FF",
                          border: "1px solid #E6EDFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Flag size={20} color="#4E8AFF" />
                      </Box>
                    </Box>
                  );
                }

                return null;
              })}

              {/* ------------------ Divider ------------------ */}
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Box sx={{ flex: 1, borderBottom: "1px solid #E5ECFF" }} />
                <Typography sx={{ mx: 2, fontSize: "13px", color: "#64748B" }}>
                  and choose anyone
                </Typography>
                <Box sx={{ flex: 1, borderBottom: "1px solid #E5ECFF" }} />
              </Box>


              {/* ------------------ OTHER DOCUMENTS ------------------ */}
              {Object.keys(documentConfig as Record<DocumentKey, any>).map((key) => {
                if (key === "pan") return null; // already rendered above

                const item = documentConfig[key as DocumentKey];
                const isActive = activeDocument === key;

                return (
                  <Box
                    key={key}
                    onClick={() => handleDocumentSelect(key as DocumentKey)}
                    sx={{
                      position: "relative",
                      borderRadius: "18px",
                      bgcolor: isActive ? "#F5F9FF" : "#FFFFFF",
                      border: isActive ? "2px solid #4E8AFF" : "1px solid #E5ECFF",
                      p: 1.5,
                      // px: 4,
                      cursor: isPanVerified ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "all 0.25s ease",
                      opacity: isPanVerified ? 1 : 0.75,
                      pointerEvents: isPanVerified ? "auto" : "none",
                      "&:hover": isPanVerified ? { backgroundColor: "#EDF4FF" } : undefined
                    }}
                  >
                    {/* Blue Left Highlight */}
                    {isActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "18px",
                          bottom: "18px",
                          left: "18px",
                          width: "4px",
                          borderRadius: "4px",
                          bgcolor: "#4E8AFF"
                        }}
                      />)}
                    {/* Title + Subtitle */}
                    <Box sx={{ ml: 4 }}>
                      <Typography fontSize="14px" fontWeight={700} color="#083D99">
                        {item.title}
                      </Typography>
                      <Typography fontSize="12px" mt={0.5} color="#64748B">
                        {item.subtitle}
                      </Typography>
                    </Box>

                    {/* Flag icon */}
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        bgcolor: "#F1F5FF",
                        border: "1px solid #E6EDFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <Flag size={20} color={isActive ? "#4E8AFF" : "#94A3B8"} />
                    </Box>
                  </Box>
                );
              })}
            </div>
          </div>

          <div className={styles.uploadSection}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-lg font-semibold text-[#0F172A]">{activeConfig.helper}</div>
                {/* <div className="text-sm text-[#64748B] mt-1">Upload {activeConfig.subtitle}</div> */}
              </div>
            </div>

            <div className={styles.uploadGrid}>
              {renderUploadCard(activeConfig.requiresBack ? "Upload front side" : `Upload ${activeConfig.helper}`, "front")}
              {activeConfig.requiresBack && renderUploadCard("Upload back side", "back")}
            </div>

            {activeConfig.inputLabels && (
              <div className="mt-5">
                <div className={styles.inputRow}>
                  {activeConfig.inputLabels.map((input) => {
                    const shouldHidePassportFileNumber =
                      isPassportDocument && input.id === "passportFileNumber" && !isPassportFrontVerified;
                    if (shouldHidePassportFileNumber) return null;

                    const isPassportFileNumberLocked =
                      isPassportDocument && input.id === "passportFileNumber" && !isPassportFrontVerified;
                    const isInputDisabled =
                      (isPanDocument && isPanVerified) || isAadhaarDocument || isPassportFileNumberLocked;

                    return (
                      <div key={input.id} className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-[#0F172A]" htmlFor={input.id}>
                          {input.label}
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className={styles.inputWrapper}>
                          {input.type === "date" && (
                            <CalendarIcon className="h-4 w-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                          )}
                          <input
                          id={input.id}
                          type={input.type === "date" ? "date" : "text"}
                          max={input.type === "date" ? new Date().toISOString().split("T")[0] : undefined}
                          className={clsx(
                            styles.textInput,
                            input.type === "date" && "pl-10",
                            isInputDisabled
                              ? "bg-gray-100 cursor-not-allowed"
                              : "",
                          )}
                          placeholder={input.placeholder}
                          value={
                            input.type === "date"
                              ? formatDateInputValue(inputs[input.id])
                              : inputs[input.id] || ""
                          }
                          disabled={isInputDisabled}
                          required
                          onChange={(event) =>
                            setInputs((prev) => ({
                              ...prev,
                              [input.id]: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            {isVerifyButtonVisible && (
              <div className="mt-6">
                <button
                  onClick={remitterVerifyClick}
                  className={clsx(
                    styles.verifyButton,
                    (isVerifyDisabled || activeVerificationState?.loading) && "opacity-60 cursor-not-allowed",
                  )}
                  disabled={isVerifyDisabled || activeVerificationState?.loading}
                >
                  {activeVerificationState?.loading ? "Verifying..." : verifyButtonLabel}
                </button>
              </div>
            )}


          {showAadhaarLinkWarning && (
            <div className="bg-[#FFFAE6] p-4 flex items-start mt-2">
                <div className="text-yellow-400 mr-4 mt-1">
                  <WarningIcon />
                </div>

                <div>
                  <h3 className="font-bold text-sm-black">PAN and Aadhar not linked</h3>
                  <p className="text-sm-black mt-3">
                    You can initiate your transaction, but it will be put on hold. We will wait for 3
                    working days for you to get this linked, post that the transaction will get cancelled
                    and the amount will be refunded.&nbsp;
                    <a
                      href="https://www.moneyhop.co/blog/all-you-need-to-know-about-pan-aadhar-linking"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#156BFF", textDecoration: "none", cursor: "pointer" }}
                    >
                      Know more
                    </a>
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>

        <div className={styles.submitFooter}>
          <button
            className={clsx(
              styles.submitButton,
              isSubmitDisabled &&
                "!from-[#E2E8F0] !to-[#E2E8F0] text-[#94A3B8] cursor-not-allowed shadow-none",
            )}
            disabled={isSubmitDisabled}
            onClick={() => {
              if (isSubmitDisabled) return;
              dispatch(remittanceActions.setModalType("KYC_SUCCESS"));
            }}
          >
            {`Submit ${completedCount} of 2`}
          </button>
        </div>

        {showLoader && (
          <div className="fixed z-50 flex-1 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
            <Lottie options={defaultAnimationOptions} height={400} width={400} ariaRole="animation" />
          </div>
        )}

      </div>
    </Modal>
  );
};
